/* Raster helpers for the offer PDF.
 *
 * jsPDF only accepts bitmaps, so everything the document shows — the brand logo
 * (an SVG), the option thumbnails and the 3D screenshot — goes through a canvas
 * first. Each helper returns a PNG data URI plus the size it should occupy on
 * the page, in points.
 */

export type PDFBitmap = {
  dataUrl: string;
  /** Width on the page, in points. */
  width: number;
  /** Height on the page, in points. */
  height: number;
};

/* Assets can be served from the widget's own origin while the host page sits on
 * another one. Without this the canvas is tainted and `toDataURL` throws, which
 * would take the whole offer down rather than just one thumbnail. */
function loadImage(url: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = "anonymous";
    image.onload = () => resolve(image);
    image.onerror = () => reject(new Error(`Cannot load image: ${url}`));
    image.src = url;
  });
}

function createCanvas(width: number, height: number) {
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width));
  canvas.height = Math.max(1, Math.round(height));
  const context = canvas.getContext("2d");
  if (!context) throw new Error("Cannot get a 2D context for the PDF bitmap");
  return { canvas, context };
}

/**
 * Draws `url` so that it occupies exactly `box`, cropping the overflowing axis —
 * the `cover` fit. The supplied artwork already carries its own background, so
 * the box is filled edge to edge instead of being letterboxed onto an invented
 * backdrop.
 *
 * Returns the canvas rather than a data URI so a caller can go on painting into
 * it; encoding is a one-way door.
 */
async function drawCover(
  url: string,
  box: { width: number; height: number },
  scale: number,
) {
  const image = await loadImage(url);
  const { canvas, context } = createCanvas(
    box.width * scale,
    box.height * scale,
  );

  const sourceRatio = image.naturalWidth / image.naturalHeight;
  const boxRatio = box.width / box.height;
  const cropWidth =
    sourceRatio > boxRatio
      ? image.naturalHeight * boxRatio
      : image.naturalWidth;
  const cropHeight =
    sourceRatio > boxRatio
      ? image.naturalHeight
      : image.naturalWidth / boxRatio;

  context.drawImage(
    image,
    (image.naturalWidth - cropWidth) / 2,
    (image.naturalHeight - cropHeight) / 2,
    cropWidth,
    cropHeight,
    0,
    0,
    canvas.width,
    canvas.height,
  );

  return { canvas, context };
}

/** The `cover` fit as a bitmap, used for the accessory thumbnails. */
export async function renderImageCover(
  url: string,
  box: { width: number; height: number },
  scale: number,
): Promise<PDFBitmap> {
  const { canvas } = await drawCover(url, box, scale);
  return { dataUrl: canvas.toDataURL("image/png"), ...box };
}

/**
 * Rasterises `url` into a circle of `diameter`, cropping it square first.
 *
 * The design clips the swatch artwork to a circle, and the mask has to be
 * applied here rather than left to the asset: the textures shipped today happen
 * to be pre-masked circles on transparency, so a square source — which is what
 * a plain material crop is — would print as a square the day one is added.
 */
export async function renderImageCircle(
  url: string,
  diameter: number,
  scale: number,
): Promise<PDFBitmap> {
  const box = { width: diameter, height: diameter };
  const { canvas, context } = await drawCover(url, box, scale);

  const radius = canvas.width / 2;
  context.globalCompositeOperation = "destination-in";
  context.beginPath();
  context.arc(radius, radius, radius, 0, Math.PI * 2);
  context.fill();

  return { dataUrl: canvas.toDataURL("image/png"), ...box };
}

/**
 * Rasterises `url` to a fixed height, keeping its aspect ratio. Used for the
 * brand logo, whose SVG carries intrinsic dimensions the design sizes by height.
 */
export async function renderImageByHeight(
  url: string,
  height: number,
  scale: number,
): Promise<PDFBitmap> {
  const image = await loadImage(url);
  const ratio =
    image.naturalWidth > 0 && image.naturalHeight > 0
      ? image.naturalWidth / image.naturalHeight
      : 1;
  const width = height * ratio;
  const { canvas, context } = createCanvas(width * scale, height * scale);
  context.drawImage(image, 0, 0, canvas.width, canvas.height);

  return { dataUrl: canvas.toDataURL("image/png"), width, height };
}

/** Alpha below this counts as background when trimming the screenshot. */
const OPAQUE_ALPHA = 8;

/**
 * Trims the fully transparent border off a screenshot and scales what is left
 * into `box`, preserving its aspect ratio.
 *
 * The viewer frames the model for the widget's own proportions, so the raw
 * screenshot carries a wide band of empty space that would make the product look
 * lost on the page. Trimming is only possible because the scene clears with
 * alpha 0; on a config with an opaque `scene.backgroundColor` every pixel is
 * opaque, the bounds come back as the full image and the result is simply the
 * untrimmed screenshot.
 */
export async function renderTrimmedScreenshot(
  dataUrl: string,
  box: { maxWidth: number; maxHeight: number },
  scale: number,
): Promise<PDFBitmap> {
  const image = await loadImage(dataUrl);
  const { canvas, context } = createCanvas(
    image.naturalWidth,
    image.naturalHeight,
  );
  context.drawImage(image, 0, 0);

  const { data } = context.getImageData(0, 0, canvas.width, canvas.height);
  let minX = canvas.width;
  let minY = canvas.height;
  let maxX = -1;
  let maxY = -1;

  for (let y = 0; y < canvas.height; y += 1) {
    for (let x = 0; x < canvas.width; x += 1) {
      if (data[(y * canvas.width + x) * 4 + 3] <= OPAQUE_ALPHA) continue;
      if (x < minX) minX = x;
      if (x > maxX) maxX = x;
      if (y < minY) minY = y;
      if (y > maxY) maxY = y;
    }
  }

  // Nothing opaque at all: an empty scene. Returning the untrimmed frame keeps
  // the page layout intact instead of dividing by a zero-sized crop.
  if (maxX < 0 || maxY < 0) {
    minX = 0;
    minY = 0;
    maxX = canvas.width - 1;
    maxY = canvas.height - 1;
  }

  const cropWidth = maxX - minX + 1;
  const cropHeight = maxY - minY + 1;
  const fit = Math.min(box.maxWidth / cropWidth, box.maxHeight / cropHeight);
  const width = cropWidth * fit;
  const height = cropHeight * fit;

  /* Embed at `scale` times the printed size and no more. The canvas is as large
   * as the viewer, so on a HiDPI screen the crop can be four or five times what
   * the page needs — pixels that only inflate the file, which then has to travel
   * as an email attachment. */
  const resolution = Math.min(1, (width * scale) / cropWidth);
  const target = createCanvas(cropWidth * resolution, cropHeight * resolution);
  target.context.drawImage(
    canvas,
    minX,
    minY,
    cropWidth,
    cropHeight,
    0,
    0,
    target.canvas.width,
    target.canvas.height,
  );

  return {
    dataUrl: target.canvas.toDataURL("image/png"),
    width,
    height,
  };
}
